/**
 * Roles y matriz de permisos booleanos.
 */
import React, { useState } from 'react';
import { initialRoleState } from '../AdminPanel';
import EditIcon from '../../assets/icons/edit.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import { studio, StudioIcon, Field, SearchField, FilterDock, FilterMeta, DockActionRow, PermSwitch, PermGroup, cols } from './studioUi';
import { useAuth } from '../../AuthContext';

const RoleManagement = ({ roles, allPermissionsList, cargarTodo, styles, API_URL, fetchWithAuth, embedded = false }) => {
    const { withBusy } = useAuth();
    const API_URL_INTERNAL = API_URL;
    const [newRole, setNewRole] = useState(initialRoleState);
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [showRoleForm, setShowRoleForm] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [roleQuery, setRoleQuery] = useState('');
    const [permQuery, setPermQuery] = useState('');

    const handlePermissionChange = (permissionId, isChecked) => {
        setNewRole(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [permissionId]: isChecked
            }
        }));
    };

    const handleRoleSubmit = async (e) => {
        e.preventDefault();
        const isEditing = !!editingRoleId;

        const url = isEditing ? `${API_URL_INTERNAL}/admin/roles/${editingRoleId}` : `${API_URL_INTERNAL}/admin/roles`;
        const method = isEditing ? 'PUT' : 'POST';

        const payload = {
            id: newRole.id,
            name: newRole.name,
            description: newRole.description,
            permissions: newRole.permissions
        };

        try {
            await withBusy(async () => {
            const res = await fetchWithAuth(url, { // Usar fetchWithAuth
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert("Operación exitosa.");
                setNewRole(initialRoleState);
                setEditingRoleId(null);
                setShowRoleForm(false);
                setPermQuery('');
                cargarTodo();
            } else {
                let data = { error: "Error desconocido al guardar rol." };
                try {
                    data = await res.json();
                } catch (jsonError) {
                    console.error("Failed to parse error response:", jsonError);
                }
                alert(`Error: ${data.error}`);
            }
            }, isEditing ? 'Guardando rol…' : 'Creando rol…');
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        }
    };

    const cancelRoleEdit = () => {
        setEditingRoleId(null);
        setNewRole(initialRoleState);
        setShowRoleForm(false);
        setPermQuery('');
    };

    const groupedPermissions = allPermissionsList.reduce((acc, perm) => {
        acc[perm.category] = acc[perm.category] || [];
        acc[perm.category].push(perm);
        return acc;
    }, {});

    const permTerm = permQuery.trim().toLowerCase();
    const visibleCategories = Object.keys(groupedPermissions).sort().filter((category) => {
        if (!permTerm) return true;
        if (category.toLowerCase().includes(permTerm)) return true;
        return groupedPermissions[category].some((perm) => perm.name.toLowerCase().includes(permTerm));
    });

    const selectedPermCount = Object.values(newRole.permissions || {}).filter(Boolean).length;
    const rolesFiltrados = roles.filter((role) => {
        const term = roleQuery.trim().toLowerCase();
        if (!term) return true;
        return (role.name || '').toLowerCase().includes(term)
            || (role.id || '').toLowerCase().includes(term)
            || (role.description || '').toLowerCase().includes(term);
    });

    const toggleCategory = (items) => {
        const allOn = items.every((perm) => newRole.permissions[perm.id]);
        setNewRole((prev) => {
            const permissions = { ...prev.permissions };
            items.forEach((perm) => {
                permissions[perm.id] = !allOn;
            });
            return { ...prev, permissions };
        });
    };

    return (
        <div>
            <div style={studio.card}>
                {!embedded && <h3 style={{ margin: '0 0 16px' }}>Gestión de Roles</h3>}

                {showRoleForm && (
                    <div style={studio.formPanel}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                            <div>
                                <h3 style={{ ...studio.formTitle, margin: 0 }}>{editingRoleId ? 'Editar rol' : 'Nuevo rol'}</h3>
                                <p style={{ ...studio.hint, marginTop: 6 }}>
                                    {selectedPermCount} {selectedPermCount === 1 ? 'permiso activo' : 'permisos activos'}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" onClick={cancelRoleEdit} style={studio.ghostBtn}>Cancelar</button>
                                <button type="submit" form="role-form" style={studio.primaryBtn}>{editingRoleId ? 'Actualizar rol' : 'Crear rol'}</button>
                            </div>
                        </div>
                        <form id="role-form" onSubmit={handleRoleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr 1.4fr'), gap: '14px' }}>
                                <Field label="ID">
                                <input
                                    placeholder="project_manager"
                                    value={newRole.id}
                                    onChange={e => setNewRole({ ...newRole, id: e.target.value })}
                                    style={studio.control}
                                    required
                                    disabled={editingRoleId !== null}
                                />
                                </Field>
                                <Field label="Nombre">
                                <input
                                    placeholder="Project Manager"
                                    value={newRole.name}
                                    onChange={e => setNewRole({ ...newRole, name: e.target.value })}
                                    style={studio.control}
                                    required
                                />
                                </Field>
                                <Field label="Descripción">
                                <input
                                    placeholder="Qué puede hacer este perfil"
                                    value={newRole.description}
                                    onChange={e => setNewRole({ ...newRole, description: e.target.value })}
                                    style={studio.control}
                                />
                                </Field>
                            </div>

                            <div style={{ marginTop: 18 }}>
                                <SearchField
                                    value={permQuery}
                                    onChange={setPermQuery}
                                    placeholder="Buscar permiso o categoría…"
                                    style={{ marginBottom: 12 }}
                                />
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                                    {visibleCategories.map((category) => {
                                        const items = groupedPermissions[category].filter((perm) => (
                                            !permTerm
                                            || category.toLowerCase().includes(permTerm)
                                            || perm.name.toLowerCase().includes(permTerm)
                                        ));
                                        return (
                                            <PermGroup
                                                key={category}
                                                title={category}
                                                count={items.filter((perm) => newRole.permissions[perm.id]).length}
                                                total={items.length}
                                                onToggleAll={() => toggleCategory(items)}
                                            >
                                                {items.map((perm) => (
                                                    <PermSwitch
                                                        key={perm.id}
                                                        label={perm.name}
                                                        checked={!!newRole.permissions[perm.id]}
                                                        onChange={(isChecked) => handlePermissionChange(perm.id, isChecked)}
                                                    />
                                                ))}
                                            </PermGroup>
                                        );
                                    })}
                                </div>
                                {visibleCategories.length === 0 && (
                                    <div style={studio.empty}>Ningún permiso coincide con la búsqueda.</div>
                                )}
                            </div>
                        </form>
                    </div>
                )}

                <FilterDock>
                    <DockActionRow
                        action={!showRoleForm ? (
                            <button type="button" onClick={() => { setShowRoleForm(true); setPermQuery(''); }} style={studio.primaryBtn}>Nuevo rol</button>
                        ) : null}
                    >
                        <SearchField value={roleQuery} onChange={setRoleQuery} placeholder="Buscar rol…" style={{ marginBottom: 0 }} />
                    </DockActionRow>
                    <FilterMeta
                        summary={`${rolesFiltrados.length} ${rolesFiltrados.length === 1 ? 'rol' : 'roles'}`}
                        chips={roleQuery ? [{ key: 'q', label: `“${roleQuery}”`, onClear: () => setRoleQuery('') }] : []}
                        onClearAll={roleQuery ? () => setRoleQuery('') : undefined}
                    />
                </FilterDock>

                {rolesFiltrados.length === 0 && <div style={studio.empty}>{roles.length === 0 ? 'Aún no hay roles.' : 'Ningún rol coincide con la búsqueda.'}</div>}
                {rolesFiltrados.length > 0 && (
                <table style={studio.table}>
                    <thead>
                        <tr>
                            <th style={studio.th}>ID</th>
                            <th style={studio.th}>Nombre</th>
                            <th style={studio.th}>Descripción</th>
                            <th style={studio.th}>Permisos</th>
                            <th style={{...studio.th, width: '100px'}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rolesFiltrados.map(role => (
                            <tr
                                key={role.id}
                                style={hoveredRow === role.id ? studio.trHover : undefined}
                                onMouseEnter={() => setHoveredRow(role.id)}
                                onMouseLeave={() => setHoveredRow(null)}
                            >
                                <td style={{...studio.td, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '0.82rem', color: '#5f6368'}}>{role.id}</td>
                                <td style={{...studio.td, fontWeight: 600}}>{role.name}</td>
                                <td style={{...studio.td, color: '#5f6368'}}>{role.description}</td>
                                <td style={studio.td}>
                                    <span style={studio.tabBadge}>
                                        {Object.values(role.permissions || {}).filter(Boolean).length}
                                    </span>
                                </td>
                                <td style={{...studio.td, whiteSpace: 'nowrap'}}>
                                    <button title="Editar rol" onClick={() => { setEditingRoleId(role.id); setNewRole(role); setShowRoleForm(true); setPermQuery(''); }} style={studio.iconBtn}>
                                        <StudioIcon src={EditIcon} alt="Editar" />
                                    </button>
                                    <button title="Eliminar rol" onClick={async () => {
                                        if (window.confirm(`¿Estás seguro de eliminar el rol "${role.name}"?`)) {
                                            await fetchWithAuth(`${API_URL_INTERNAL}/admin/roles/${role.id}`, { method: 'DELETE' });
                                            cargarTodo();
                                        }
                                    }} style={studio.iconBtnDanger}>
                                        <StudioIcon src={TrashIcon} alt="Eliminar" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                )}
            </div>
        </div>
    );
};

export default RoleManagement;